import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import type { IAddPricesToWorkOrderProps } from '../helpersAndTypes';

import { uerAddUerToWorkOrderObjectsGql } from './addPricesToWorkOrderQueries';

const requestId = 'addPricesToWorkOrder';

export const useAddPricesToWorkOrder = (props: IAddPricesToWorkOrderProps) => {
    const { workOrderObjectIds, chapter, workOrderId, constructionTypeId, uerInputs, onClose, reloadWorkOrderTable } =
        props;

    const dispatch = useDispatch();
    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();
    const onSuccess = useCallback(() => {
        onClose();
        reloadWorkOrderTable();
    }, [onClose, reloadWorkOrderTable]);

    const [, status, , request] = useRequest({
        requestId,
        query: uerAddUerToWorkOrderObjectsGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const isLoading = status === RequestStatus.FETCHING;
    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );
    const addPricesToWorkOrderHandler = useCallback(() => {
        if (chapter && uerInputs?.length) {
            dispatch(
                request({
                    workOrderObjectIds,
                    chapter,
                    workOrderId,
                    constructionTypeId,
                    uerInputs,
                }),
            );
            sendGpnAnalyticsEvent({
                componentId: 'rim_NZ_add to order price button_pressed',
                ...analyticsData,
                orderStatus: WorkOrderStatusEnum.OnFormation,
                addType: WorkOrderPriceTypeEnum.Uer,
            });
        }
    }, [
        analyticsData,
        chapter,
        constructionTypeId,
        dispatch,
        request,
        sendGpnAnalyticsEvent,
        uerInputs,
        workOrderId,
        workOrderObjectIds,
    ]);

    const [resetAddUerFromWorkOrder] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            resetAddUerFromWorkOrder(undefined);
        },
        [resetAddUerFromWorkOrder],
    );

    return {
        addPricesToWorkOrderHandler,
        isLoading,
    };
};
